import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { useAppState } from './AppState.js';
import { PageIntro } from './components.js';
import { mapError } from './errors.js';

export function CreateLeaguePage() {
  const { client, player, canCreateLeague, refreshIdentity, setActiveLeague, setBanner } = useAppState();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startingHandicap, setStartingHandicap] = useState('');

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!client) {
        throw new Error('API client unavailable');
      }
      return client.createLeague({
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        startingHandicap: Number(startingHandicap),
      });
    },
    onSuccess: async ({ league }) => {
      await refreshIdentity(true);
      setActiveLeague(league.id);
      setBanner('success', `${league.name} created. Set up a season to get started.`);
      navigate(`/leagues/${league.id}/admin/seasons`);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  if (player && !canCreateLeague) {
    return <Navigate replace to="/leagues" />;
  }

  return (
    <>
      <PageIntro title="Create League" subtitle="You will be the admin of this league." />
      <section className="panel stack">
        <form className="stack" onSubmit={(event) => {
          event.preventDefault();
          createMutation.mutate();
        }}>
          <div className="field">
            <label htmlFor="new-league-name">League name</label>
            <input id="new-league-name" value={name} onChange={(event) => setName(event.target.value)}
              required maxLength={100} />
          </div>
          <div className="field">
            <label htmlFor="new-league-description">Description (optional)</label>
            <input id="new-league-description" value={description}
              onChange={(event) => setDescription(event.target.value)} maxLength={500} />
          </div>
          <div className="field">
            <label htmlFor="new-league-handicap">Your starting handicap index</label>
            <input id="new-league-handicap" type="number" min={-10} max={27} step="0.1" required
              value={startingHandicap} onChange={(event) => setStartingHandicap(event.target.value)} />
            <p className="muted">Enter your current index between -10 and 27. This seeds your league handicap history.</p>
          </div>
          <div className="inline-actions">
            <button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create League'}
            </button>
            <Link className="nav-link" to="/leagues">Cancel</Link>
          </div>
        </form>
      </section>
    </>
  );
}
